// The RBP add to cart's two dialogs, SL1.0's confirm and done modals
// (`rbp_npr_confirm_add_modal.htm`, `rbp_npr_add_done_modal.htm`) on the design-system `Modal`:
//   - the confirmation, "Add RBP items to Cart(s)?", with a Depot / RBP items row per depot that
//     has a line and the actions "Add items to cart(s)" and "Cancel";
//   - the done step, "RBP Items successfully added to Cart(s)", with the depots written, the
//     selections that could not be added and stay selected, an "Open cart" action per depot and
//     "Back to RBP Shopping List".
// SL1.0's done modal linked each depot's cart and switched the session's depot on the way. Here
// "Open cart" does the same, but only on the shopper's explicit click: it selects that row's cart
// and opens the Order Review, which reads the active cart. Adding never changes the active cart
// behind the shopper's back. The row's cart is the one offered entry of the row's range (Chill
// and Fruit & Veg share depot 25, so the range tells them apart), else the primary entry at the
// row's depot among entries that name no range. When several entries share the range, the
// active one is kept if it is among them; otherwise the choice is ambiguous. When no entry is the
// row's cart, the choice is ambiguous, or the entry offers several accounts and none is chosen
// there yet, it opens the depot chooser rather than a wrong cart.
import { useNavigate } from 'react-router';
import type { OrderingEntry } from '../../api/ordering-choices.client';
import type { RbpCartAdditionDepot } from '../../api/rbp.client';
import { useSessionStore } from '../../auth/useSessionStore';
import { Banner, Button, Modal } from '../../design-system';
import { CHOOSE_ROUTE, primaryEntryAtDepot } from '../../ordering/orderingChoices';
import { selectOrderingChoice } from '../../ordering/orderingSession';
import type { RbpCartAdditionState } from '../../rbp/useRbpCartAddition';
import styles from './RbpCartAdditionDialog.module.css';

const ORDER_REVIEW_ROUTE = '/cart';

/**
 * The offered entry whose cart a row's lines went to, or null when none is, or when several
 * entries share the row's range (a Value Centre's DEPOT and LSN entries can) and none of them is
 * the active one.
 */
function entryForRow(
  entries: readonly OrderingEntry[],
  row: Pick<RbpCartAdditionDepot, 'assortmentCode' | 'depotCode'>,
  activeEntryKey: string | null,
): OrderingEntry | null {
  const range = row.assortmentCode.toUpperCase();
  const ofRange = entries.filter((entry) => entry.assortmentCode?.toUpperCase() === range);
  if (ofRange.length === 1) return ofRange[0] ?? null;
  if (ofRange.length > 1) {
    return ofRange.find((entry) => entry.key === activeEntryKey) ?? null;
  }
  // An entry naming another range is another cart, even at the row's depot. The server sends
  // `null` when it names no range (an older server omits the key). An empty depot code is the
  // client's default for a missing one, so it names no depot.
  if (row.depotCode === '') return null;
  const atDepot = entries.filter(
    (entry) => entry.depotCode === row.depotCode && !entry.assortmentCode,
  );
  const first = atDepot[0];
  return first === undefined ? null : primaryEntryAtDepot(atDepot, first.depotId);
}

/** Select `row`'s cart and return where to go: the Order Review, or the depot chooser. */
function openCartFor(row: Pick<RbpCartAdditionDepot, 'assortmentCode' | 'depotCode'>): string {
  const { orderingChoices, activeEntryKey, activeTradingAccount } = useSessionStore.getState();
  if (orderingChoices === null) return CHOOSE_ROUTE;
  const entry = entryForRow(orderingChoices.entries, row, activeEntryKey);
  if (entry === null) return CHOOSE_ROUTE;
  // Already on this entry: keep the account chosen there (a Value Centre may offer several).
  const accountCode =
    entry.key === activeEntryKey ? (activeTradingAccount?.customerAccountCode ?? null) : null;
  return selectOrderingChoice(entry.key, accountCode) ? ORDER_REVIEW_ROUTE : CHOOSE_ROUTE;
}

function DepotTable({
  caption,
  countHeading,
  depots,
  openCartLinks,
}: {
  readonly caption: string;
  readonly countHeading: string;
  readonly depots: ReadonlyArray<RbpCartAdditionDepot & { readonly addedCount?: number }>;
  readonly openCartLinks: boolean;
}) {
  const navigate = useNavigate();
  return (
    <table className={styles.table}>
      <caption className={styles.caption}>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Depot</th>
          <th scope="col" className={styles.count}>
            {countHeading}
          </th>
          {openCartLinks ? (
            <th scope="col">
              <span className={styles.visuallyHidden}>Cart</span>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {depots.map((depot) => (
          <tr key={depot.assortmentCode} data-assortment-code={depot.assortmentCode}>
            <th scope="row" className={styles.depot}>
              {depot.displayName}
            </th>
            <td className={styles.count}>{depot.addedCount ?? depot.lineCount}</td>
            {openCartLinks ? (
              <td className={styles.link}>
                <button
                  type="button"
                  aria-label={`Open cart, ${depot.displayName}`}
                  onClick={() => navigate(openCartFor(depot))}
                >
                  Open cart
                </button>
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RbpCartAdditionDialog({
  state,
  onConfirm,
  onClose,
}: {
  readonly state: RbpCartAdditionState;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  if (state.status === 'confirm' || state.status === 'adding') {
    const adding = state.status === 'adding';
    return (
      <Modal
        open
        // While the add runs the dialog stays: closing it would not stop the write.
        onClose={adding ? () => undefined : onClose}
        title="Add RBP items to Cart(s)?"
        description="Here is a breakdown of items to be added across your depot carts."
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={adding}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onConfirm} disabled={adding} aria-busy={adding}>
              {adding ? 'Adding…' : 'Add items to cart(s)'}
            </Button>
          </>
        }
      >
        <DepotTable
          caption="Items to add per depot"
          countHeading="RBP items"
          depots={state.preview.depots}
          openCartLinks={false}
        />
      </Modal>
    );
  }
  if (state.status === 'done') {
    const { result } = state;
    return (
      <Modal
        open
        onClose={onClose}
        title="RBP Items successfully added to Cart(s)"
        description="Please complete all orders within depot pages."
        footer={
          <Button variant="primary" onClick={onClose}>
            Back to RBP Shopping List
          </Button>
        }
      >
        <DepotTable
          caption="Items added per depot"
          countHeading="Items added by RBP"
          depots={result.depots}
          openCartLinks
        />
        {result.selectionsCleared ? null : (
          // The carts took the lines but the list could not clear them: a second add would add
          // the quantities again.
          <div className={styles.warning}>
            <Banner type="warning">
              These products are still selected on your list. Clear the selection before adding
              again, or their quantities will be added twice.
            </Banner>
          </div>
        )}
        {result.notAddedCount > 0 ? (
          <p className={styles.notAdded} data-not-added>
            {result.notAddedCount === 1
              ? '1 selected product could not be added and stays selected.'
              : `${result.notAddedCount} selected products could not be added and stay selected.`}
          </p>
        ) : null}
      </Modal>
    );
  }
  return null;
}
